import { SkaterInfo, SubmittedWaiver, Waiver as WaiverType } from '@types';
import { DateOfBirthInput, SignaturePad } from '_atoms';
import { useAppSelector } from '_hooks';
import { SeasonDetailsService } from '_services';
import HelperMethods from '_utils/HelperMethods';
import {
  compressSignature,
  getCompressionSettings,
} from '_utils/signatureCompression';
import jsPDF from 'jspdf';
import React, { useEffect, useRef, useState } from 'react';
import {
  MdAdd,
  MdClear,
  MdEdit,
  MdExpandMore,
  MdGavel,
  MdRemove,
} from 'react-icons/md';
import MySubmittedWaiversList from './MySubmittedWaiversList';

interface WaiverNewProps {
  skatersInfo?: SkaterInfo[];
  onPrevious?: () => void;
  onContinue?: (data: any) => void;
  isInFlow?: boolean; // If true, show "Next" button instead of "Submit Waiver"
}

interface MinorParticipant {
  id: string;
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  relationship?: string;
}

interface AdultParticipant {
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  phoneNumber: string;
  email: string;
  emergencyContactPhone: string;
  hasReadWaiver: boolean;
  accepted: boolean;
  signature: string;
  signedBy: string;
  skaterName?: string;
  isAdult?: boolean;
  relationship?: string;
}

const normalizePhoneDigits = (phone: string | null | undefined): string => {
  if (!phone) return '';
  return phone.replace(/\D/g, '').slice(0, 10);
};

const WaiverNew: React.FC<WaiverNewProps> = ({
  skatersInfo = [],
  onPrevious,
  onContinue,
  isInFlow = false,
}) => {
  // Check if user is authenticated and load account contact info
  const { isAuthenticated, accountDetails, currentUser } = useAppSelector(
    state => state.auth,
  );

  // Generate unique barcodeId for this waiver
  const [barcodeId] = useState(() => {
    const waiverBarcode = HelperMethods.generateRandom6DigitNumber();
    return `wv${waiverBarcode.toString()}`;
  });

  const [currentWaiver, setCurrentWaiver] = useState<WaiverType>({
    id: '',
    waiverDetails: '',
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showExpandedWaiver, setShowExpandedWaiver] = useState(false);
  const [showRemoveConfirm, setShowRemoveConfirm] = useState<string | null>(
    null,
  );
  const [attachedWaivers, setAttachedWaivers] = useState<
    Record<string, SubmittedWaiver>
  >({});
  const [showExistingWaivers, setShowExistingWaivers] = useState(true);

  // Track whether the user manually edited contact fields so later profile
  // loads (or skatersInfo updates) do not overwrite their edits.
  const contactFieldsEditedRef = useRef({
    email: false,
    phoneNumber: false,
  });

  // Ref for signature pad
  const signaturePadRef = useRef<any>(null);

  // Adult participant state (FR-18: Default to one adult)
  const [adult, setAdult] = useState<AdultParticipant>({
    firstName: '',
    lastName: '',
    dateOfBirth: '',
    phoneNumber: '',
    email: '',
    emergencyContactPhone: '',
    hasReadWaiver: false,
    accepted: false,
    signature: '',
    signedBy: '',
  });

  // Minors state (FR-19: Minors covered by this waiver)
  const [minors, setMinors] = useState<MinorParticipant[]>([]);

  const profileEmail =
    accountDetails?.email || currentUser?.attributes?.email || '';
  const profilePhone = normalizePhoneDigits(
    accountDetails?.phoneNumber || currentUser?.attributes?.phone_number || '',
  );

  // Prefill adult email/phone from the logged-in account profile.
  // Edits stay on this waiver only and are never written back to the account.
  useEffect(() => {
    if (!isAuthenticated) return;

    setAdult(prev => {
      const next = { ...prev };
      let changed = false;

      if (!contactFieldsEditedRef.current.email && profileEmail) {
        if (prev.email !== profileEmail) {
          next.email = profileEmail;
          changed = true;
        }
      }

      if (!contactFieldsEditedRef.current.phoneNumber && profilePhone) {
        if (prev.phoneNumber !== profilePhone) {
          next.phoneNumber = profilePhone;
          changed = true;
        }
      }

      return changed ? next : prev;
    });
  }, [isAuthenticated, profileEmail, profilePhone]);

  // Initialize minors from skatersInfo if provided
  useEffect(() => {
    if (skatersInfo.length > 0) {
      const adultSkater = skatersInfo.find(s => {
        const age = HelperMethods.calculateAge(s.dateOfBirth);
        return age >= 18; // Only auto-populate if actually 18 or older
      });

      if (adultSkater) {
        setAdult(prev => {
          const useProfileEmail =
            isAuthenticated &&
            !!profileEmail &&
            !contactFieldsEditedRef.current.email;
          const useProfilePhone =
            isAuthenticated &&
            !!profilePhone &&
            !contactFieldsEditedRef.current.phoneNumber;

          return {
            ...prev,
            firstName: adultSkater.firstName,
            lastName: adultSkater.lastName,
            dateOfBirth: adultSkater.dateOfBirth,
            // Profile contact wins when logged in; otherwise use skater info
            // unless the user already edited the field.
            email: contactFieldsEditedRef.current.email
              ? prev.email
              : useProfileEmail
                ? profileEmail
                : adultSkater.email || prev.email || '',
            phoneNumber: contactFieldsEditedRef.current.phoneNumber
              ? prev.phoneNumber
              : useProfilePhone
                ? profilePhone
                : normalizePhoneDigits(adultSkater.phoneNumber) ||
                  prev.phoneNumber ||
                  '',
          };
        });
      }

      const minorSkaters = skatersInfo.filter(s => {
        const age = HelperMethods.calculateAge(s.dateOfBirth);
        return age < 18; // Include all under 18 in minors list
      });

      const minorParticipants: MinorParticipant[] = minorSkaters.map(
        (skater, index) => ({
          id: `minor-${index + 1}`,
          firstName: skater.firstName,
          lastName: skater.lastName,
          dateOfBirth: skater.dateOfBirth,
        }),
      );

      setMinors(minorParticipants);
    }
  }, [skatersInfo, isAuthenticated, profileEmail, profilePhone]);

  // Fetch waiver content
  useEffect(() => {
    const fetchWaiver = async () => {
      try {
        setLoading(true);

        const fetchPromise =
          SeasonDetailsService.getCurrentWaiver() as unknown as Promise<WaiverType>;
        const responseData = (await fetchPromise) as WaiverType;
        console.log('Waiver details:', responseData.waiverDetails);

        setCurrentWaiver(responseData);
      } catch (err) {
        setError('Failed to load waiver content');
        console.error('Error fetching waiver:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchWaiver();
  }, []);

  const getAdultDateOfBirthError = (dateOfBirth: string): string | null => {
    if (!dateOfBirth) return null;
    if (!HelperMethods.isPlausibleDateOfBirth(dateOfBirth)) {
      return 'Enter a valid date of birth';
    }
    const age = HelperMethods.calculateAge(dateOfBirth);
    if (age < 18) {
      return 'You must be 18 years or older';
    }
    return null;
  };

  const getMinorDateOfBirthError = (dateOfBirth: string): string | null => {
    if (!dateOfBirth) return null;
    if (!HelperMethods.isPlausibleDateOfBirth(dateOfBirth)) {
      return 'Enter a valid date of birth';
    }
    const age = HelperMethods.calculateAge(dateOfBirth);
    if (age < 3 || age >= 18) {
      return 'Date of Birth must be under 18 for minors';
    }
    return null;
  };

  const isMinorAgeValid = (dateOfBirth: string): boolean => {
    return getMinorDateOfBirthError(dateOfBirth) === null && !!dateOfBirth;
  };

  const isAdultAgeValid = (dateOfBirth: string): boolean => {
    return getAdultDateOfBirthError(dateOfBirth) === null && !!dateOfBirth;
  };

  // Helper function to filter out minors with attached waivers
  const getMinorsWithoutAttachedWaivers = (): MinorParticipant[] => {
    return minors.filter(minor => {
      const minorKey = `${minor.firstName}_${minor.lastName}`;
      return !attachedWaivers[minorKey];
    });
  };

  // Check if all skaters have attached waivers
  const allSkatersHaveWaivers =
    skatersInfo.length > 0 &&
    skatersInfo.every(skater => {
      const skaterKey = `${skater.firstName}_${skater.lastName}`;
      return attachedWaivers[skaterKey];
    });

  // FR-22: Add Minor button functionality
  const addMinor = () => {
    const newMinor: MinorParticipant = {
      id: `minor-${Date.now()}`,
      firstName: '',
      lastName: '',
      dateOfBirth: '',
    };
    setMinors(prev => [...prev, newMinor]);
  };

  // FR-20, FR-21: Remove minor with confirmation
  const removeMinor = (minorId: string) => {
    setShowRemoveConfirm(minorId);
  };

  const confirmRemoveMinor = (minorId: string) => {
    setMinors(prev => prev.filter(minor => minor.id !== minorId));
    setShowRemoveConfirm(null);
  };

  const cancelRemoveMinor = () => {
    setShowRemoveConfirm(null);
  };

  const updateMinor = (
    minorId: string,
    field: keyof MinorParticipant,
    value: string,
  ) => {
    setMinors(prev =>
      prev.map(minor =>
        minor.id === minorId ? { ...minor, [field]: value } : minor,
      ),
    );
  };

  const updateAdult = (
    field: keyof AdultParticipant,
    value: string | boolean,
  ) => {
    if (field === 'email' || field === 'phoneNumber') {
      contactFieldsEditedRef.current[field] = true;
    }
    setAdult(prev => ({ ...prev, [field]: value }));
  };

  // Clear signature
  const clearSignature = () => {
    if (signaturePadRef.current) {
      signaturePadRef.current.clear();
    }
    updateAdult('signature', '');
  };

  // Validate phone number format
  const isValidPhoneNumber = (phoneNumber: string): boolean => {
    // Remove all non-digit characters
    const digitsOnly = phoneNumber.replace(/\D/g, '');

    // Check if phone number is exactly 10 digits
    if (!digitsOnly || digitsOnly.length !== 10) {
      return false;
    }

    // Check for invalid patterns
    const invalidPatterns = [
      /^0{10}$/, // All zeros
      /^1{10}$/, // All ones
      /^2{10}$/, // All twos
      /^3{10}$/, // All threes
      /^4{10}$/, // All fours
      /^5{10}$/, // All fives
      /^6{10}$/, // All sixes
      /^7{10}$/, // All sevens
      /^8{10}$/, // All eights
      /^9{10}$/, // All nines
      /^(\d)\1{9}$/, // Repeated single digit (e.g., 1111111111)
      /^(123456789|987654321)/, // Sequential patterns
      /^000/, // Starts with 000
    ];

    // Check if phone number matches any invalid pattern
    for (const pattern of invalidPatterns) {
      if (pattern.test(digitsOnly)) {
        return false;
      }
    }

    return true;
  };

  // Helper function to parse HTML and maintain formatting
  const parseHTMLToFormattedContent = (
    html: string,
  ): Array<{
    text: string;
    bold?: boolean;
    italic?: boolean;
    underline?: boolean;
    fontSize?: number;
    indent?: number;
    isList?: boolean;
  }> => {
    if (!html) return [];

    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;

    const content: Array<any> = [];

    const parseNode = (node: Node, inherited: any = {}) => {
      if (node.nodeType === Node.TEXT_NODE) {
        const text = node.textContent?.trim();
        if (text) {
          content.push({
            text,
            ...inherited,
          });
        }
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        const element = node as HTMLElement;
        const style: any = { ...inherited };

        // Handle different HTML elements
        switch (element.tagName.toLowerCase()) {
          case 'strong':
          case 'b':
            style.bold = true;
            break;
          case 'em':
          case 'i':
            style.italic = true;
            break;
          case 'u':
            style.underline = true;
            break;
          case 'h1':
            style.bold = true;
            style.fontSize = 16;
            break;
          case 'h2':
            style.bold = true;
            style.fontSize = 14;
            break;
          case 'h3':
            style.bold = true;
            style.fontSize = 12;
            break;
          case 'li':
            style.isList = true;
            style.indent = 5;
            break;
          case 'p':
            // Paragraph - add line break
            break;
          case 'br':
            content.push({ text: '\n' });
            return;
        }

        // Process children
        Array.from(element.childNodes).forEach(child => {
          parseNode(child, style);
        });

        // Add line breaks after block elements
        if (
          ['p', 'h1', 'h2', 'h3', 'li', 'div'].includes(
            element.tagName.toLowerCase(),
          )
        ) {
          content.push({ text: '\n' });
        }
      }
    };

    Array.from(tempDiv.childNodes).forEach(node => parseNode(node));
    return content;
  };

  // Helper function to add formatted text to PDF
  const addFormattedTextToPDF = (
    pdf: any,
    content: Array<any>,
    startY: number,
    margin: number,
    maxWidth: number,
    pageHeight: number,
  ): number => {
    let yPosition = startY;
    const lineHeight = 5;
    const defaultFontSize = 10;

    content.forEach((item, index) => {
      // Check if we need a new page
      if (yPosition > pageHeight - 40) {
        pdf.addPage();
        yPosition = margin;
      }

      // Set font style
      const fontSize = item.fontSize || defaultFontSize;
      pdf.setFontSize(fontSize);

      if (item.bold && item.italic) {
        pdf.setFont('helvetica', 'bolditalic');
      } else if (item.bold) {
        pdf.setFont('helvetica', 'bold');
      } else if (item.italic) {
        pdf.setFont('helvetica', 'italic');
      } else {
        pdf.setFont('helvetica', 'normal');
      }

      // Handle text content
      let text = item.text;

      // Add bullet for list items
      if (item.isList && text !== '\n') {
        text = `• ${text}`;
      }

      // Skip empty lines except explicit newlines
      if (!text || (text.trim() === '' && text !== '\n')) {
        return;
      }

      // Handle newlines
      if (text === '\n') {
        yPosition += lineHeight;
        return;
      }

      // Split text into lines that fit within the width
      const indent = item.indent || 0;
      const effectiveWidth = maxWidth - indent;
      const words = text.split(' ');
      let currentLine = '';

      words.forEach((word: string, wordIndex: number) => {
        const testLine = currentLine ? `${currentLine} ${word}` : word;
        const testWidth = pdf.getTextWidth(testLine);

        if (testWidth > effectiveWidth && currentLine) {
          // Print current line
          pdf.text(currentLine, margin + indent, yPosition);
          yPosition += lineHeight;

          // Check if we need a new page
          if (yPosition > pageHeight - 40) {
            pdf.addPage();
            yPosition = margin;
          }

          currentLine = word;
        } else {
          currentLine = testLine;
        }

        // Print last word if it's the last one
        if (wordIndex === words.length - 1 && currentLine) {
          pdf.text(currentLine, margin + indent, yPosition);

          // Only add line height if this isn't the last item or if next item is a newline
          if (index < content.length - 1 && content[index + 1].text !== '\n') {
            yPosition += lineHeight;
          }
        }
      });

      // Add underline if needed
      if (item.underline && currentLine) {
        const textWidth = pdf.getTextWidth(currentLine);
        pdf.line(
          margin + indent,
          yPosition + 1,
          margin + indent + textWidth,
          yPosition + 1,
        );
      }
    });

    return yPosition;
  };

  const generatePDF = async (): Promise<Blob> => {
    const pdf = new jsPDF('p', 'mm', 'a4');
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const margin = 20;
    let yPosition = margin;

    // Add title
    pdf.setFontSize(20);
    pdf.setFont('helvetica', 'bold');
    pdf.text(
      'Modesto On Ice Skating Liability Waiver',
      pageWidth / 2,
      yPosition,
      {
        align: 'center',
      },
    );
    yPosition += 10;

    // Waiver number for staff reference
    pdf.setFontSize(14);
    pdf.setFont('helvetica', 'bold');
    pdf.text(`Waiver Number: ${barcodeId}`, pageWidth / 2, yPosition, {
      align: 'center',
    });
    yPosition += 12;

    // Add date and time
    pdf.setFontSize(12);
    pdf.setFont('helvetica', 'normal');
    const currentDateTime = new Date();
    const currentDate = currentDateTime.toLocaleDateString();
    const currentTime = currentDateTime.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
    pdf.text(`Date: ${currentDate}`, margin, yPosition);
    yPosition += 5;
    pdf.text(`Time: ${currentTime}`, margin, yPosition);
    yPosition += 15;

    // Adult name
    pdf.setFontSize(16);
    pdf.setFont('helvetica', 'bold');
    pdf.text(
      `Waiver for: ${adult.firstName} ${adult.lastName}`,
      margin,
      yPosition,
    );
    yPosition += 8;

    // Adult date of birth and age
    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'normal');
    const adultAge = HelperMethods.calculateAge(adult.dateOfBirth);
    pdf.text(
      `Date of Birth: ${adult.dateOfBirth} (Age: ${adultAge})`,
      margin,
      yPosition,
    );
    yPosition += 10;

    // Filter minors without attached waivers (used for both PDF content and filename)
    const minorsWithoutAttachedWaivers = getMinorsWithoutAttachedWaivers();

    // Add minors information if any (excluding those with attached waivers)
    if (minorsWithoutAttachedWaivers.length > 0) {
      pdf.setFontSize(12);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Minors covered by this waiver:', margin, yPosition);
      yPosition += 8;

      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      minorsWithoutAttachedWaivers.forEach((minor, index) => {
        const age = HelperMethods.calculateAge(minor.dateOfBirth);
        pdf.text(
          `- ${minor.firstName} ${minor.lastName}`,
          margin + 5,
          yPosition,
        );
        yPosition += 5;
        pdf.text(
          `  Date of Birth: ${minor.dateOfBirth} (Age: ${age})`,
          margin + 5,
          yPosition,
        );
        yPosition += 5;
        pdf.text(
          `  Relationship: ${minor.relationship || 'Not specified'}`,
          margin + 5,
          yPosition,
        );
        yPosition += 5;
      });
      yPosition += 5;
    }

    // Signature information
    pdf.setFontSize(12);
    pdf.setFont('helvetica', 'bold');
    pdf.text('Signature Information:', margin, yPosition);
    yPosition += 8;

    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'normal');
    pdf.text(`Signed by: ${adult.signedBy}`, margin, yPosition);
    yPosition += 5;
    pdf.text(`Relationship: Self`, margin, yPosition);
    yPosition += 5;
    pdf.text(`Email: ${adult.email}`, margin, yPosition);
    yPosition += 5;
    pdf.text(`Phone: ${adult.phoneNumber}`, margin, yPosition);
    yPosition += 5;
    pdf.text(
      `Emergency Contact: ${adult.emergencyContactPhone}`,
      margin,
      yPosition,
    );
    yPosition += 5;
    pdf.text(`Date: ${currentDate}`, margin, yPosition);
    yPosition += 5;
    pdf.text(`Time: ${currentTime}`, margin, yPosition);
    yPosition += 5;
    pdf.text(
      `Read and Accepted: ${adult.hasReadWaiver ? 'Yes' : 'No'}`,
      margin,
      yPosition,
    );
    yPosition += 5;
    pdf.text(
      `Terms Accepted: ${adult.accepted ? 'Yes' : 'No'}`,
      margin,
      yPosition,
    );
    yPosition += 10;

    // Add signature image if available
    if (adult.signature) {
      try {
        // Compress signature specifically for PDF
        const compressedSignature = await compressSignature(
          adult.signature,
          getCompressionSettings('pdf'),
        );

        const signatureImg = new Image();
        signatureImg.src = compressedSignature;

        await new Promise(resolve => {
          signatureImg.onload = () => {
            const imgWidth = 60;
            const imgHeight =
              (signatureImg.height * imgWidth) / signatureImg.width;

            if (yPosition + imgHeight > pageHeight - 20) {
              pdf.addPage();
              yPosition = margin;
            }

            pdf.text('Digital Signature:', margin, yPosition);
            yPosition += 5;
            pdf.addImage(
              compressedSignature,
              'JPEG',
              margin,
              yPosition,
              imgWidth,
              imgHeight,
            );
            yPosition += imgHeight + 5;
            resolve(true);
          };
          signatureImg.onerror = () => {
            pdf.text(
              'Digital Signature: [Signature captured]',
              margin,
              yPosition,
            );
            yPosition += 5;
            resolve(true);
          };
        });
      } catch (error) {
        pdf.text('Digital Signature: [Signature captured]', margin, yPosition);
        yPosition += 5;
      }
    }

    // Waiver content with formatting
    if (!currentWaiver.waiverDetails) {
      console.error('No waiver details available for PDF generation');
      throw new Error('No waiver details available');
    }

    // Parse HTML and maintain formatting
    const formattedContent = parseHTMLToFormattedContent(
      currentWaiver.waiverDetails,
    );
    const maxWidth = pageWidth - 2 * margin;

    // Add formatted content to PDF
    yPosition = addFormattedTextToPDF(
      pdf,
      formattedContent,
      yPosition,
      margin,
      maxWidth,
      pageHeight,
    );

    // Generate filename for individual waiver
    const timestampDate = new Date();
    const timestamp = timestampDate
      .toISOString()
      .replace(/[:.]/g, '-')
      .slice(0, 19);
    let signeeName = `${adult.firstName}_${adult.lastName}`.replace(
      /\s+/g,
      '_',
    );

    // Include minors' names in filename if any (using already filtered list)

    if (minorsWithoutAttachedWaivers.length > 0) {
      const minorNames = minorsWithoutAttachedWaivers
        .map(minor =>
          `${minor.firstName}_${minor.lastName}`.replace(/\s+/g, '_'),
        )
        .join('_');
      signeeName = `${signeeName}_with_${minorNames}`;
    }

    const fileName = `waiver_${signeeName}_${timestamp}.pdf`;

    // Convert PDF to File object
    const pdfBlob = pdf.output('blob');
    const pdfFile = new File([pdfBlob], fileName, { type: 'application/pdf' });

    console.log('Generated PDF File:', {
      name: pdfFile.name,
      size: pdfFile.size,
      type: pdfFile.type,
      lastModified: pdfFile.lastModified,
    });

    return pdfBlob;
  };

  const isFormValid = (): boolean => {
    // Check adult fields (FR-5: Mandatory fields)
    if (
      !adult.firstName.trim() ||
      !adult.lastName.trim() ||
      !adult.dateOfBirth.trim() ||
      !adult.phoneNumber.trim() ||
      !adult.email.trim() ||
      !adult.emergencyContactPhone.trim() ||
      !adult.hasReadWaiver ||
      !adult.accepted ||
      !adult.signature.trim() ||
      !adult.signedBy.trim()
    ) {
      return false;
    }

    // Validate adult age (must be 18+)
    if (!isAdultAgeValid(adult.dateOfBirth)) {
      return false;
    }

    // Validate email format
    if (!HelperMethods.isValidEmail(adult.email)) {
      return false;
    }

    // Validate phone numbers
    if (!isValidPhoneNumber(adult.phoneNumber)) {
      return false;
    }
    if (!isValidPhoneNumber(adult.emergencyContactPhone)) {
      return false;
    }

    // Emergency contact phone cannot be the same as primary phone
    if (adult.phoneNumber === adult.emergencyContactPhone) {
      return false;
    }

    // Check minors (FR-25: Date of Birth validation)
    // Only validate minors that don't have attached waivers
    for (const minor of minors) {
      const minorKey = `${minor.firstName}_${minor.lastName}`;
      // Skip validation for minors with attached waivers
      if (attachedWaivers[minorKey]) {
        continue;
      }

      if (
        !minor.firstName.trim() ||
        !minor.lastName.trim() ||
        !minor.dateOfBirth.trim() ||
        !minor.relationship
      ) {
        return false;
      }
      if (!isMinorAgeValid(minor.dateOfBirth)) {
        return false;
      }
    }

    return true;
  };

  const handleAttachWaiver = (waiver: SubmittedWaiver, skaterId?: string) => {
    if (!skaterId) return;

    // Get the skater info
    const skater = skatersInfo.find(s => s.id === skaterId);
    if (!skater) return;

    // Create a key using firstName_lastName
    const skaterKey = `${skater.firstName}_${skater.lastName}`;

    // Update attached waivers mapping
    const updatedAttachedWaivers = {
      ...attachedWaivers,
      [skaterKey]: waiver,
    };
    setAttachedWaivers(updatedAttachedWaivers);

    // Don't auto-navigate - let the user attach waivers for all skaters first
  };

  const handleUnattachWaiver = (skaterKey?: string) => {
    if (!skaterKey) return;

    // skaterKey is now the firstName_lastName format
    const updatedAttachedWaivers = { ...attachedWaivers };
    delete updatedAttachedWaivers[skaterKey];
    setAttachedWaivers(updatedAttachedWaivers);

    // Just update the state, don't navigate
  };

  const handleSubmit = async () => {
    const skatersWithoutWaivers = skatersInfo.filter(skater => {
      const skaterKey = `${skater.firstName}_${skater.lastName}`;
      return !attachedWaivers[skaterKey];
    });

    // Scenario 1: All skaters have attached waivers (no form needed)
    if (skatersInfo.length > 0 && skatersWithoutWaivers.length === 0) {
      const waiverData = {
        attachedWaivers,
        isExistingWaiver: true,
      };
      onContinue?.(waiverData);
      return;
    }

    // Scenario 2: No skaters info OR need to create new waiver
    // Check if form needs to be filled out
    const needsNewWaiver =
      skatersInfo.length === 0 || skatersWithoutWaivers.length > 0;

    if (needsNewWaiver && !isFormValid()) {
      // Provide specific error messages
      const errorMessage = 'Please fix the following issues:\n';
      const errors: string[] = [];

      if (
        !adult.firstName.trim() ||
        !adult.lastName.trim() ||
        !adult.dateOfBirth.trim() ||
        !adult.email.trim() ||
        !adult.signedBy.trim()
      ) {
        errors.push('- Complete all required adult information fields');
      }

      if (adult.dateOfBirth && !isAdultAgeValid(adult.dateOfBirth)) {
        const adultDobError = getAdultDateOfBirthError(adult.dateOfBirth);
        errors.push(`- ${adultDobError || 'Adult must be 18 years or older'}`);
      }

      if (!isValidPhoneNumber(adult.phoneNumber)) {
        errors.push(
          '- Enter a valid 10-digit phone number (not all same digits or invalid pattern)',
        );
      }

      if (!isValidPhoneNumber(adult.emergencyContactPhone)) {
        errors.push('- Enter a valid 10-digit emergency contact phone number');
      }

      if (adult.phoneNumber === adult.emergencyContactPhone) {
        errors.push(
          '- Emergency contact phone number must be different from your phone number',
        );
      }

      if (!adult.hasReadWaiver || !adult.accepted) {
        errors.push('- Read and accept the waiver terms');
      }

      if (!adult.signature.trim()) {
        errors.push('- Provide your digital signature');
      }

      for (const minor of minors) {
        const minorKey = `${minor.firstName}_${minor.lastName}`;
        // Skip validation for minors with attached waivers
        if (attachedWaivers[minorKey]) {
          continue;
        }

        if (
          !minor.firstName.trim() ||
          !minor.lastName.trim() ||
          !minor.dateOfBirth.trim() ||
          !minor.relationship
        ) {
          errors.push(
            '- Complete all minor information fields including relationship',
          );
          break;
        }
        if (!isMinorAgeValid(minor.dateOfBirth)) {
          const minorDobError = getMinorDateOfBirthError(minor.dateOfBirth);
          errors.push(
            `- ${minorDobError || 'Ensure all minors are under 18 years old'}`,
          );
          break;
        }
      }

      alert(errorMessage + errors.join('\n'));
      return;
    }

    try {
      // Generate the signed PDF if needed
      let signedPDF: File | undefined;
      let fileName: string | undefined;
      let updatedAdult: AdultParticipant | undefined;

      if (needsNewWaiver) {
        const signedPDFBlob = await generatePDF();

        // Convert blob to File object
        const submitDate = new Date();
        const timestamp = submitDate
          .toISOString()
          .replace(/[:.]/g, '-')
          .slice(0, 19);
        let signeeName = `${adult.firstName}_${adult.lastName}`.replace(
          /\s+/g,
          '_',
        );

        // Only include minors without attached waivers in filename
        const minorsWithoutAttachedWaivers = getMinorsWithoutAttachedWaivers();

        if (minorsWithoutAttachedWaivers.length > 0) {
          const minorNames = minorsWithoutAttachedWaivers
            .map(minor =>
              `${minor.firstName}_${minor.lastName}`.replace(/\s+/g, '_'),
            )
            .join('_');
          signeeName = `${signeeName}_with_${minorNames}`;
        }

        fileName = `waiver_${signeeName}_${timestamp}.pdf`;
        signedPDF = new File([signedPDFBlob], fileName, {
          type: 'application/pdf',
        });

        // Populate additional fields in the adult object
        updatedAdult = {
          ...adult,
          skaterName: `${adult.firstName} ${adult.lastName}`,
          isAdult: true,
          relationship: 'Self',
        };
      }

      // Build waiver data that can handle both scenarios
      // Always include attachedWaivers if they exist, even in mixed scenarios
      const waiverData: any = {
        // Determine the overall type
        isExistingWaiver: !needsNewWaiver,
        // Flag to indicate if this is a mixed scenario
        hasMixedWaivers:
          Object.keys(attachedWaivers).length > 0 && needsNewWaiver,
      };

      // Include new waiver data if created
      if (needsNewWaiver && updatedAdult) {
        // Only include minors without attached waivers
        const minorsWithoutAttachedWaivers = getMinorsWithoutAttachedWaivers();

        waiverData.adult = updatedAdult;
        waiverData.minors = minorsWithoutAttachedWaivers;
        waiverData.waiverId = currentWaiver.id;
        waiverData.barcodeId = barcodeId;
      }

      // Include PDF if created
      if (signedPDF) {
        waiverData.signedPDF = signedPDF;
        waiverData.fileName = fileName;
      }

      // Always include attached waivers if they exist
      if (Object.keys(attachedWaivers).length > 0) {
        waiverData.attachedWaivers = attachedWaivers;
      }

      onContinue?.(waiverData);
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Error generating PDF. Please try again.');
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-4 space-y-6">
      {/* Header */}
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          Liability Waiver
        </h2>
        <p className="text-sm text-gray-700 mb-2">
          Waiver Number:{' '}
          <span className="font-mono font-semibold text-blue-700 tracking-wide">
            {barcodeId}
          </span>
        </p>
        <p className="text-gray-600">
          This Liability Waiver applies to one adult, either as a participant or
          as the parent/legal guardian of the minor skater(s) listed below. Each
          additional adult (18 years or older) must complete a separate waiver.
        </p>
      </div>

      {/* Existing Waivers Section - Only show if user is logged in AND in a booking flow */}
      {isAuthenticated && showExistingWaivers && isInFlow && (
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-medium text-gray-900">
                Use Existing Waiver
              </h3>
              <p className="text-sm text-gray-600 mt-1">
                Select a previously submitted waiver or create a new one below
              </p>
            </div>
            <button
              onClick={() => setShowExistingWaivers(false)}
              className="text-sm text-gray-500 hover:text-gray-700"
            >
              Hide
            </button>
          </div>

          {/* Show waiver status for each skater */}
          {skatersInfo.length > 0 && (
            <div className="mb-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
              <h4 className="text-sm font-medium text-gray-900 mb-2">
                Waiver Status:
              </h4>
              <div className="space-y-2">
                {skatersInfo.map(skater => {
                  const skaterKey = `${skater.firstName}_${skater.lastName}`;
                  const hasWaiver = !!attachedWaivers[skaterKey];
                  const waiver = attachedWaivers[skaterKey];
                  return (
                    <div
                      key={skater.id}
                      className={`flex items-center justify-between p-2 rounded ${
                        hasWaiver ? 'bg-green-50' : 'bg-red-50'
                      }`}
                    >
                      <span
                        className={`text-sm font-medium ${hasWaiver ? 'text-green-800' : 'text-red-800'}`}
                      >
                        {skater.firstName} {skater.lastName}
                      </span>
                      {hasWaiver ? (
                        <span className="text-xs text-green-700">
                          ✓ Attached: {waiver.skaterName}
                        </span>
                      ) : (
                        <span className="text-xs text-red-700">
                          ⚠ Waiver Required
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <MySubmittedWaiversList
            onAttach={handleAttachWaiver}
            onUnattach={handleUnattachWaiver}
            attachedWaiverIds={Object.fromEntries(
              Object.entries(attachedWaivers).map(([skaterId, waiver]) => [
                skaterId,
                waiver.id,
              ]),
            )}
            skatersInfo={skatersInfo}
          />
        </div>
      )}

      {/* Show button to reveal existing waivers if hidden */}
      {isAuthenticated && !showExistingWaivers && isInFlow && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-blue-800">
              Already have a waiver? Attach an existing one instead
            </p>
            <button
              onClick={() => setShowExistingWaivers(true)}
              className="px-4 py-2 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 transition-colors"
            >
              Show Existing Waivers
            </button>
          </div>
        </div>
      )}

      {/* Divider - Hide if all skaters have attached waivers */}
      {isAuthenticated && isInFlow && !allSkatersHaveWaivers && (
        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-gray-300"></div>
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-4 bg-gray-50 text-gray-500">
              Or create a new waiver
            </span>
          </div>
        </div>
      )}

      {/* Waiver Content - Hide if all skaters have attached waivers */}
      {(!isAuthenticated || !isInFlow || !allSkatersHaveWaivers) && (
        <>
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-2">
                <MdGavel className="text-blue-600" />
                <h3 className="text-lg font-medium text-gray-900">
                  Waiver Terms
                </h3>
                <span className="text-sm text-gray-600">
                  · Waiver Number:{' '}
                  <span className="font-mono font-semibold text-blue-700">
                    {barcodeId}
                  </span>
                </span>
              </div>
              <button
                onClick={() => setShowExpandedWaiver(true)}
                className="flex items-center space-x-1 text-blue-600 hover:text-blue-800 text-sm font-medium"
              >
                <MdExpandMore className="w-4 h-4" />
                <span>View Full Waiver</span>
              </button>
            </div>

            <div className="max-h-64 overflow-y-auto border border-gray-200 rounded p-4 bg-gray-50">
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                  <span className="ml-2 text-gray-600">Loading waiver...</span>
                </div>
              ) : error ? (
                <div className="text-center py-8">
                  <p className="text-red-600 mb-4">{error}</p>
                  <button
                    onClick={() => window.location.reload()}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    Reload
                  </button>
                </div>
              ) : (
                <div
                  className="prose prose-sm max-w-none"
                  dangerouslySetInnerHTML={{
                    __html: currentWaiver.waiverDetails,
                  }}
                />
              )}
            </div>
          </div>

          {/* Adult Section */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <div className="flex items-center space-x-2 mb-4">
              <MdEdit className="text-blue-600" />
              <h3 className="text-lg font-medium text-gray-900">
                Adult Participant, Parent, or Legal Guardian (18+)
              </h3>
            </div>

            {/* FR-5: Mandatory fields for adult waivers */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  First Name *
                </label>
                <input
                  type="text"
                  value={adult.firstName}
                  onChange={e => updateAdult('firstName', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    adult.firstName.trim()
                      ? 'border-gray-300'
                      : 'border-red-500'
                  }`}
                  placeholder="Enter first name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Last Name *
                </label>
                <input
                  type="text"
                  value={adult.lastName}
                  onChange={e => updateAdult('lastName', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    adult.lastName.trim() ? 'border-gray-300' : 'border-red-500'
                  }`}
                  placeholder="Enter last name"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Date of Birth *
                </label>
                <DateOfBirthInput
                  value={adult.dateOfBirth}
                  onChange={value => {
                    updateAdult('dateOfBirth', value);
                  }}
                  min={HelperMethods.getMinDateOfBirth()}
                  max={HelperMethods.getMaxDateOfBirth()}
                  className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    adult.dateOfBirth && !isAdultAgeValid(adult.dateOfBirth)
                      ? 'border-red-500 bg-red-50'
                      : adult.dateOfBirth
                        ? 'border-gray-300'
                        : 'border-red-500'
                  }`}
                />
                {adult.dateOfBirth &&
                  getAdultDateOfBirthError(adult.dateOfBirth) && (
                    <p className="text-red-600 text-xs mt-1">
                      {getAdultDateOfBirthError(adult.dateOfBirth)}
                    </p>
                  )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Phone Number *
                </label>
                <input
                  type="tel"
                  value={adult.phoneNumber}
                  onChange={e => {
                    const numericValue = e.target.value.replace(/[^0-9]/g, '');
                    if (numericValue.length <= 10) {
                      updateAdult('phoneNumber', numericValue);
                    }
                  }}
                  maxLength={10}
                  className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    adult.phoneNumber && !isValidPhoneNumber(adult.phoneNumber)
                      ? 'border-red-500 bg-red-50'
                      : adult.phoneNumber
                        ? 'border-gray-300'
                        : 'border-red-500'
                  }`}
                  placeholder="Enter phone number"
                />
                {adult.phoneNumber &&
                  !isValidPhoneNumber(adult.phoneNumber) && (
                    <p className="text-red-600 text-xs mt-1">
                      Please enter a valid 10-digit phone number (not all same
                      digits)
                    </p>
                  )}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email Address *
                </label>
                <input
                  type="email"
                  value={adult.email}
                  onChange={e => updateAdult('email', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    !adult.email.trim() ||
                    (adult.email.trim() &&
                      !HelperMethods.isValidEmail(adult.email))
                      ? 'border-red-500 focus:ring-red-500 focus:border-red-500'
                      : 'border-gray-300'
                  }`}
                  placeholder="Enter email address"
                />
                {adult.email.trim() &&
                  !HelperMethods.isValidEmail(adult.email) && (
                    <p className="mt-1 text-sm text-red-600">
                      Please enter a valid email address
                    </p>
                  )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Emergency Contact Phone *
                </label>
                <input
                  type="tel"
                  value={adult.emergencyContactPhone}
                  onChange={e => {
                    const numericValue = e.target.value.replace(/[^0-9]/g, '');
                    if (numericValue.length <= 10) {
                      updateAdult('emergencyContactPhone', numericValue);
                    }
                  }}
                  maxLength={10}
                  className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    adult.emergencyContactPhone &&
                    (!isValidPhoneNumber(adult.emergencyContactPhone) ||
                      adult.emergencyContactPhone === adult.phoneNumber)
                      ? 'border-red-500 bg-red-50'
                      : adult.emergencyContactPhone
                        ? 'border-gray-300'
                        : 'border-red-500'
                  }`}
                  placeholder="Enter emergency contact phone"
                />
                {adult.emergencyContactPhone &&
                  !isValidPhoneNumber(adult.emergencyContactPhone) && (
                    <p className="text-red-600 text-xs mt-1">
                      Please enter a valid 10-digit phone number (not all same
                      digits)
                    </p>
                  )}
                {adult.emergencyContactPhone &&
                  isValidPhoneNumber(adult.emergencyContactPhone) &&
                  adult.emergencyContactPhone === adult.phoneNumber && (
                    <p className="text-red-600 text-xs mt-1">
                      Emergency contact phone must be different from your phone
                      number
                    </p>
                  )}
              </div>
            </div>

            {/* Waiver Acceptance */}
            <div className="space-y-3 mb-4">
              <div className="flex items-start space-x-3">
                <input
                  type="checkbox"
                  id="readWaiver"
                  checked={adult.hasReadWaiver}
                  onChange={e => updateAdult('hasReadWaiver', e.target.checked)}
                  className={`mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 rounded ${
                    adult.hasReadWaiver ? 'border-gray-300' : 'border-red-500'
                  }`}
                />
                <label htmlFor="readWaiver" className="text-sm text-gray-700">
                  I have read and understand the complete liability waiver above
                  *
                </label>
              </div>
              <div className="flex items-start space-x-3">
                <input
                  type="checkbox"
                  id="acceptWaiver"
                  checked={adult.accepted}
                  onChange={e => updateAdult('accepted', e.target.checked)}
                  className={`mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 rounded ${
                    adult.accepted ? 'border-gray-300' : 'border-red-500'
                  }`}
                />
                <label htmlFor="acceptWaiver" className="text-sm text-gray-700">
                  I accept the terms and conditions of this liability waiver *
                </label>
              </div>
            </div>
          </div>

          {/* FR-19: Minors covered by this waiver */}
          <div className="bg-white border border-gray-200 rounded-lg p-4 md:p-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4 mb-4">
              <h3 className="text-xl font-bold text-gray-900">
                Minors covered by this waiver
              </h3>
            </div>

            {(() => {
              // Filter out minors that have attached waivers
              const minorsWithoutAttachedWaivers =
                getMinorsWithoutAttachedWaivers();

              return minorsWithoutAttachedWaivers.length === 0 ? (
                <p className="text-gray-500 text-sm">
                  {minors.length === 0
                    ? 'No minors added yet. Click "Add Minor" to add participants under 18.'
                    : 'All minors have attached waivers. No new waiver needed for minors.'}
                </p>
              ) : (
                <div className="space-y-4">
                  {minorsWithoutAttachedWaivers.map(minor => (
                    <div
                      key={minor.id}
                      className="p-4 border border-gray-200 rounded-lg bg-gray-50"
                    >
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="font-medium text-gray-900 text-sm md:text-base">
                          Minor{' '}
                          {minorsWithoutAttachedWaivers.indexOf(minor) + 1}
                        </h4>
                        {/* FR-20: Remove option */}
                        <button
                          onClick={() => removeMinor(minor.id)}
                          className="flex items-center space-x-1 px-3 py-2 text-sm text-red-600 hover:text-red-800 hover:bg-red-50 rounded-md border border-red-200 min-h-[40px] active:bg-red-100 transition-colors"
                        >
                          <MdRemove className="w-4 h-4 flex-shrink-0" />
                          <span className="whitespace-nowrap">Remove</span>
                        </button>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4 mb-4">
                        <div>
                          <label className="block text-xs md:text-sm font-medium text-gray-700 mb-1">
                            First Name *
                          </label>
                          <input
                            type="text"
                            value={minor.firstName}
                            onChange={e =>
                              updateMinor(minor.id, 'firstName', e.target.value)
                            }
                            className={`w-full px-3 py-2.5 md:py-2 text-sm md:text-base border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 min-h-[44px] ${
                              minor.firstName.trim()
                                ? 'border-gray-300'
                                : 'border-red-500'
                            }`}
                            placeholder="First name"
                          />
                        </div>
                        <div>
                          <label className="block text-xs md:text-sm font-medium text-gray-700 mb-1">
                            Last Name *
                          </label>
                          <input
                            type="text"
                            value={minor.lastName}
                            onChange={e =>
                              updateMinor(minor.id, 'lastName', e.target.value)
                            }
                            className={`w-full px-3 py-2.5 md:py-2 text-sm md:text-base border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 min-h-[44px] ${
                              minor.lastName.trim()
                                ? 'border-gray-300'
                                : 'border-red-500'
                            }`}
                            placeholder="Last name"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
                        <div>
                          <label className="block text-xs md:text-sm font-medium text-gray-700 mb-1">
                            Date of Birth *
                          </label>
                          <DateOfBirthInput
                            value={minor.dateOfBirth}
                            onChange={value => {
                              updateMinor(minor.id, 'dateOfBirth', value);
                            }}
                            min={HelperMethods.getMinDateOfBirth()}
                            max={HelperMethods.getMaxDateOfBirth()}
                            className={`w-full px-3 py-2.5 md:py-2 text-sm md:text-base border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 min-h-[44px] ${
                              minor.dateOfBirth &&
                              !isMinorAgeValid(minor.dateOfBirth)
                                ? 'border-red-500 bg-red-50'
                                : minor.dateOfBirth
                                  ? 'border-gray-300'
                                  : 'border-red-500'
                            }`}
                          />
                          {/* FR-25: Age validation */}
                          {minor.dateOfBirth &&
                            getMinorDateOfBirthError(minor.dateOfBirth) && (
                              <p className="text-red-600 text-xs mt-1">
                                {getMinorDateOfBirthError(minor.dateOfBirth)}
                              </p>
                            )}
                        </div>
                        <div>
                          <label className="block text-xs md:text-sm font-medium text-gray-700 mb-1">
                            Relationship to Minor *
                          </label>
                          <select
                            value={minor.relationship || ''}
                            onChange={e =>
                              updateMinor(
                                minor.id,
                                'relationship',
                                e.target.value,
                              )
                            }
                            className={`w-full px-3 py-2.5 md:py-2 text-sm md:text-base border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 min-h-[44px] ${
                              minor.relationship
                                ? 'border-gray-300'
                                : 'border-red-500'
                            }`}
                          >
                            <option value="">Select relationship</option>
                            <option value="Parent">Parent</option>
                            <option value="Legal Guardian">
                              Legal Guardian
                            </option>
                          </select>
                          {!minor.relationship && (
                            <p className="text-red-600 text-xs mt-1">
                              Please select relationship to minor
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              );
            })()}
            <div className="mt-4 flex justify-start">
              {/* FR-22: Add Minor button */}
              <button
                onClick={addMinor}
                className="flex items-center justify-center space-x-2 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[44px] w-full sm:w-auto whitespace-nowrap"
              >
                <MdAdd className="w-4 h-4 flex-shrink-0" />
                <span className="text-sm md:text-base">
                  Add Minor (Ages 3-17)
                </span>
              </button>
            </div>
          </div>

          {/* Signature Information */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <div className="flex items-center space-x-2 mb-4">
              <MdEdit className="text-blue-600" />
              <h3 className="text-lg font-medium text-gray-900">
                Signature Information
              </h3>
            </div>

            {/* Show who should sign if adult name is filled */}
            {(adult.firstName.trim() || adult.lastName.trim()) && (
              <div className="mb-3 p-3 bg-blue-50 border border-blue-200 rounded-md">
                <p className="text-lg text-blue-900 font-bold">
                  Waiver must be signed by{' '}
                  {[adult.firstName.trim(), adult.lastName.trim()]
                    .filter(Boolean)
                    .join(' ')}
                </p>
              </div>
            )}
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Full Name (for signature) *
            </label>
            <input
              type="text"
              value={adult.signedBy}
              onChange={e => updateAdult('signedBy', e.target.value)}
              className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 mb-3 ${
                adult.signedBy.trim() ? 'border-gray-300' : 'border-red-500'
              }`}
              placeholder="Enter your full name"
            />

            <label className="block text-sm font-medium text-gray-700 mb-1">
              Digital Signature *
            </label>
            <div
              className={`border rounded-md p-2 bg-white ${
                adult.signature ? 'border-gray-300' : 'border-red-500'
              }`}
            >
              <SignaturePad
                ref={signaturePadRef}
                height={200}
                redrawOnResize
                options={{
                  penColor: 'blue',
                  backgroundColor: 'white',
                }}
                compressionOptions={{
                  format: 'image/jpeg',
                  quality: 0.6,
                }}
                canvasProps={{
                  className:
                    'sigCanvas w-full h-48 border border-gray-200 rounded',
                }}
                onAutoSave={dataUrl => updateAdult('signature', dataUrl)}
                autoSaveDelay={1000}
              />
            </div>
            <div className="flex items-center space-x-2 mt-2">
              <button
                type="button"
                onClick={clearSignature}
                className="flex items-center space-x-1 px-3 py-1 text-sm text-red-600 border border-red-300 rounded hover:bg-red-50 active:bg-red-100 transition-colors"
              >
                <MdClear className="w-4 h-4" />
                <span>Clear Signature</span>
              </button>
              <p className="text-xs text-gray-500">
                Sign above using your mouse or touch screen
              </p>
            </div>
          </div>
        </>
      )}

      {/* FR-26: Additional adult note - Hide if all skaters have attached waivers */}
      {skatersInfo.length > 0 &&
        (!isAuthenticated || !isInFlow || !allSkatersHaveWaivers) && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <p className="text-sm text-yellow-800">
              <strong>Note:</strong> Additional adult(s) (18+) must complete
              their own waiver via the ModestoOnIce.com website.
            </p>
          </div>
        )}

      {/* Expanded Waiver Modal */}
      {showExpandedWaiver && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] flex flex-col shadow-2xl">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div>
                <h3 className="text-lg font-medium text-gray-900">
                  Complete Liability Waiver
                </h3>
                <p className="text-sm text-gray-600 mt-1">
                  Waiver Number:{' '}
                  <span className="font-mono font-semibold text-blue-700">
                    {barcodeId}
                  </span>
                </p>
              </div>
              <button
                onClick={() => setShowExpandedWaiver(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <MdClear className="w-6 h-6" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-6">
              <div
                className="prose prose-lg max-w-none"
                dangerouslySetInnerHTML={{
                  __html: currentWaiver.waiverDetails,
                }}
              />
            </div>
            <div className="p-6 border-t border-gray-200">
              <button
                onClick={() => setShowExpandedWaiver(false)}
                className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Remove Confirmation Modal - FR-21 */}
      {showRemoveConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6 shadow-2xl">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Confirm Removal
            </h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to remove this minor?
            </p>
            <div className="flex space-x-3">
              <button
                onClick={cancelRemoveMinor}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={() => confirmRemoveMinor(showRemoveConfirm)}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                Remove
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Helper function to check if all skaters have waivers */}
      {(() => {
        // Use component-level variable, but also allow proceeding if no skaters
        const canProceedWithExistingWaivers =
          skatersInfo.length === 0 || allSkatersHaveWaivers;

        // If all skaters have attached waivers, we can proceed without creating a new waiver
        // Otherwise, we need to validate the form
        const canProceed = canProceedWithExistingWaivers || isFormValid();

        return (
          /* Navigation */
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4 pt-6">
            <div className="flex items-center space-x-3">
              {onPrevious && (
                <button
                  onClick={onPrevious}
                  className="px-6 py-3 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300"
                >
                  Back
                </button>
              )}
              {/* <button
                onClick={handlePreviewPDF}
                disabled={!adult.firstName || !adult.lastName || !adult.signature}
                className="flex items-center space-x-2 px-4 py-3 text-blue-600 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 disabled:bg-gray-100 disabled:text-gray-400 disabled:border-gray-200 disabled:cursor-not-allowed"
              >
                <MdDownload className="w-4 h-4" />
                <span>Preview PDF</span>
              </button> */}
            </div>
            <button
              onClick={handleSubmit}
              disabled={!canProceed}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              {isInFlow ? 'Next' : 'Submit Waiver'}
            </button>
          </div>
        );
      })()}
    </div>
  );
};

export default WaiverNew;
